import { useState, type FormEvent } from 'react';
import DialogError from '@/shared/ui/DialogError';
import FormField from '@/shared/ui/FormField';
import { primaryButtonClass, secondaryButtonClass } from '@/shared/ui/formClasses';
import Modal, { ModalBody, ModalFooter, ModalForm } from '@/shared/ui/Modal';
import type { RoleOption } from '../model/roles';
import { NEW_USER_FORM, newUserErrors, type NewUserErrors, type NewUserForm } from '../model/users';

type CreateUserDialogProps = {
  roleOptions: RoleOption[];
  submitting: boolean;
  error?: string;
  onSubmit: (form: NewUserForm) => void;
  onClose: () => void;
};

/** La creazione di un utente: al primo accesso dovrà cambiare la password iniziale. */
export default function CreateUserDialog({
  roleOptions,
  submitting,
  error,
  onSubmit,
  onClose,
}: CreateUserDialogProps) {
  const [form, setForm] = useState(NEW_USER_FORM);
  const [errors, setErrors] = useState<NewUserErrors>({});
  const set = (name: 'username' | 'email' | 'password') => (value: string) =>
    setForm((current) => ({ ...current, [name]: value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const found = newUserErrors(form);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(form);
  };

  return (
    <Modal title="Nuovo utente" onClose={onClose} closeDisabled={submitting}>
      <ModalForm noValidate onSubmit={handleSubmit}>
        <ModalBody>
          {error && <DialogError>{error}</DialogError>}
          <FormField
            id="user-username"
            label="Username"
            required
            autoFocus
            autoComplete="off"
            value={form.username}
            onChange={set('username')}
            error={errors.username}
          />
          <FormField
            id="user-email"
            label="Email"
            type="email"
            required
            autoComplete="off"
            value={form.email}
            onChange={set('email')}
            error={errors.email}
          />
          <FormField
            id="user-password"
            label="Password iniziale"
            type="password"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
            error={errors.password}
            hint="Al primo accesso l'utente dovrà cambiarla."
          />
          <FormField
            id="user-role"
            label="Ruolo"
            value={form.role}
            // Le opzioni sono i ruoli di `roleOptions`: il DOM restituisce solo stringhe.
            onChange={(role) =>
              setForm((current) => ({ ...current, role: role as NewUserForm['role'] }))
            }
            options={roleOptions}
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={secondaryButtonClass}
          >
            Annulla
          </button>
          <button type="submit" disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Creazione…' : 'Crea utente'}
          </button>
        </ModalFooter>
      </ModalForm>
    </Modal>
  );
}
